import { get, set, del, delMany, keys, entries, createStore } from 'idb-keyval'
import { __error } from '../init/debug'

export const DEFAULT_CACHE_TTL = 604800 // 7 days, in seconds
export const OIDC_SESSION_KEY = 'oidcSession'
export const LIB_TOKENS_KEY = 'lib-tokens'
export const PREDEFINED_ROLES_DEF_KEY = 'predef-roles-def'

export type CacheInitOptions = {
  user_id: string
}
export type CacheOptions = {
  global?: boolean
  ttl?: number
}
export type SyncCache = {
  prefix: string
  get<T = any>(key: string): T | undefined
  set<T = any>(key: string, value: T, options?: CacheOptions): SyncCache
  delete(key: string): SyncCache
  clear(): SyncCache
}
export type AsyncCache = {
  session: SyncCache
  get<T = any>(key: string): Promise<T | undefined>
  set<T = any>(key: string, value: T, options?: CacheOptions): Promise<AsyncCache>
  delete(key: string): Promise<AsyncCache>
  clear(): Promise<AsyncCache>
}
export type CacheValueType<T = any> = {
  value: T
  cached: number
  expiry?: number
}

const prefixes = {
  cache: '',
  idb: '',
  session: '',
}

/* session storage sync cache */
const checkSessionNotInitError = () => {
  if (!prefixes.session) {
    throw new Error('session cache not inited')
  }
}
export const sessionCache = {
  get<T = any>(key: string): T | undefined {
    checkSessionNotInitError()
    const prefixedKey = prefixes.session + key
    try {
      const json = window.sessionStorage.getItem(prefixedKey)
      const data = json ? JSON.parse(json) as CacheValueType<T> : undefined
      if (data?.value === undefined || (data?.expiry && data.expiry < Date.now())) {
        window.sessionStorage.removeItem(prefixedKey)
        return undefined
      }
      return data.value as T
    }
    catch (e) {
      __error('failed to get sync cache', e)
      return undefined
    }
  },
  set<T = any>(key: string, value: T, options?: CacheOptions) {
    if (value === null || value === undefined) {
      return sessionCache.delete(key)
    }
    checkSessionNotInitError()
    const cached = Date.now()
    const ttl = Math.round(options?.ttl || 0)
    const data: CacheValueType<T> = {
      value,
      cached,
      expiry: ttl ? cached + ttl * 1000 : undefined,
    }
    const prefixedKey = prefixes.session + key
    window.sessionStorage.setItem(prefixedKey, JSON.stringify(data))
    return sessionCache
  },
  delete(key: string) {
    checkSessionNotInitError()
    const prefixedKey = prefixes.session + key
    window.sessionStorage.removeItem(prefixedKey)
    return sessionCache
  },
  clear() {
    checkSessionNotInitError()
    const keys = Object.keys(window.sessionStorage)
    for (const key of keys) {
      if (key.startsWith(prefixes.session)) {
        window.sessionStorage.removeItem(key)
      }
    }
    return sessionCache
  },
}

const maintainDbCache = async () => {
  const kv = await entries<string, any>(idbStore)
  const purgeKeys = []
  const now = Date.now()
  for (const [key, data] of kv) {
    if (!key.startsWith(prefixes.idb)) {
      purgeKeys.push(key)
    }
    else if (data?.expiry && data.expiry < now) {
      purgeKeys.push(key)
    }
  }
  await delMany(purgeKeys, idbStore)
}

const memCached = {} as Record<string, any>
const memAsyncCache = {
  session: sessionCache,
  async get<T = any>(key: string): Promise<T | undefined> {
    const prefixedKey = prefixes.idb + key
    try {
      const data = memCached[prefixedKey]
      if (data?.value === undefined || (data?.expiry && data.expiry < Date.now())) {
        del(prefixedKey, idbStore).catch((e) => {
          __error('failed to delete expired cache', e)
        })
        return undefined
      }
      return data.value
    }
    catch (e) {
      __error('failed to get async cache', e)
      return undefined
    }
  },
  async set<T = any>(key: string, value: T, options?: CacheOptions): Promise<AsyncCache> {
    if (value === null || value === undefined) {
      return memAsyncCache.delete(key)
    }
    const cached = Date.now()
    const ttl = Math.round((options?.ttl ?? DEFAULT_CACHE_TTL) || 0)
    const data: CacheValueType<T> = {
      value,
      cached,
      expiry: ttl ? cached + ttl * 1000 : undefined,
    }
    const prefixedKey = prefixes.idb + key
    memCached[prefixedKey] = data
    return memAsyncCache
  },
  async delete(key: string) {
    const prefixedKey = prefixes.idb + key
    delete memCached[prefixedKey]
    return memAsyncCache
  },
  async clear() {
    const allKeys = Object.keys(memCached)
    for (const key of allKeys) {
      if (key.startsWith(prefixes.idb)) {
        delete memCached[key]
      }
    }
    return memAsyncCache
  },
} as AsyncCache

/* idb async cache */
const checkIdbCacheNotInitError = () => {
  if (!idbStore || !prefixes.idb) {
    throw new Error('cache not inited')
  }
}
const idbStore = 'indexedDB' in window ? createStore('sparky', 'cache') : undefined
const idbCache = {
  session: sessionCache,
  async get<T = any>(key: string): Promise<T | undefined> {
    checkIdbCacheNotInitError()
    const prefixedKey = prefixes.idb + key
    try {
      const data = await get<CacheValueType<T>>(prefixedKey, idbStore)
      if (data?.value === undefined || (data?.expiry && data.expiry < Date.now())) {
        del(prefixedKey, idbStore).catch((e) => {
          __error('failed to delete expired cache', e)
        })
        return undefined
      }
      return data.value
    }
    catch (e) {
      __error('failed to get async cache', e)
      return undefined
    }
  },
  async set<T = any>(key: string, value: T, options?: CacheOptions): Promise<AsyncCache> {
    if (value === null || value === undefined) {
      return idbCache.delete(key)
    }
    checkIdbCacheNotInitError()
    const cached = Date.now()
    const ttl = Math.round((options?.ttl ?? DEFAULT_CACHE_TTL) || 0)
    const data: CacheValueType<T> = {
      value,
      cached,
      expiry: ttl ? cached + ttl * 1000 : undefined,
    }
    const prefixedKey = prefixes.idb + key
    await set(prefixedKey, data, idbStore)
    return idbCache
  },
  async delete(key: string) {
    checkIdbCacheNotInitError()
    const prefixedKey = prefixes.idb + key
    await del(prefixedKey, idbStore)
    return idbCache
  },
  async clear() {
    checkIdbCacheNotInitError()
    const allKeys = await keys<string>(idbStore)
    const clearKeys = allKeys.filter(k => k.startsWith(prefixes.idb))
    if (clearKeys.length) {
      await delMany(clearKeys, idbStore)
    }
    return idbCache
  },
} as AsyncCache

/* cache storage async cache */
const EXPIRY_HEADER = 'x-sparky-expiry'
const CACHE_KEY_HEADER = 'x-sparky-cache'
const cacheStorePromise = 'caches' in window ? caches.open('sparky') : undefined
const checkCacheStorageNotInitError = () => {
  if (!cacheStorePromise || !prefixes.cache) {
    throw new Error('cache not inited')
  }
}
const cacheStorageCache = {
  session: sessionCache,
  async get<T = any>(key: string): Promise<T | undefined> {
    checkCacheStorageNotInitError()
    const prefixedKey = prefixes.cache + key
    try {
      const cacheStore = await cacheStorePromise!
      const resp = await cacheStore.match(prefixedKey)
      if (!resp?.ok) {
        return undefined
      }
      const expiry = +(resp.headers.get(EXPIRY_HEADER) || 0)
      if (expiry > 0 && +expiry < Date.now()) {
        cacheStore.delete(prefixedKey).catch((e) => {
          __error('failed to delete expired cache', e)
        })
        return undefined
      }
      return resp.json()
    }
    catch (e) {
      __error('failed to get async cache', e)
      return undefined
    }
  },
  async set<T = any>(key: string, value: T, options?: CacheOptions): Promise<AsyncCache> {
    if (value === null || value === undefined) {
      return cacheStorageCache.delete(key)
    }
    checkCacheStorageNotInitError()
    const cacheStore = await cacheStorePromise!
    const cached = Date.now()
    const ttl = Math.round((options?.ttl ?? DEFAULT_CACHE_TTL) || 0)
    const json = JSON.stringify(value)
    const prefixedKey = prefixes.cache + key
    const headers = {
      [CACHE_KEY_HEADER]: prefixedKey,
      'content-type': 'application/json',
      'content-length': json.length.toString(),
      'date': new Date(cached).toUTCString(),
    } as Record<string, string>
    if (ttl > 0) {
      headers['cache-control'] = `max-age=${Math.round(ttl)}`
      headers[EXPIRY_HEADER] = (cached + ttl * 1000).toString()
    }
    const resp = new Response(json, {
      headers,
    })
    await cacheStore.put(prefixedKey, resp)
    return cacheStorageCache
  },
  async delete(key: string) {
    checkCacheStorageNotInitError()
    const prefixedKey = prefixes.cache + key
    const cacheStore = await cacheStorePromise!
    await cacheStore.delete(prefixedKey)
    return cacheStorageCache
  },
  async clear() {
    checkCacheStorageNotInitError()
    const cacheStore = await cacheStorePromise!
    const allKeys = await cacheStore.keys()
    const clearKeys = allKeys.filter(req => req.url.startsWith(prefixes.cache))
    if (clearKeys.length) {
      await Promise.all(clearKeys.map(req => cacheStore.delete(req)))
    }
    return cacheStorageCache
  },
} as AsyncCache

const maintainStorageCache = async () => {
  const cacheStore = await cacheStorePromise!
  const allCacheResp = await cacheStore.matchAll()
  const now = Date.now()
  let needToCleanKeys = false
  // __info('maintain cache for', prefixes.cache, 'and remove other users')
  const shouldBeDeleted = (resp: Response) => {
    const cacheKey = resp.headers.get(CACHE_KEY_HEADER)
    if (!cacheKey) {
      needToCleanKeys = true
      return false
    }
    if (!cacheKey.startsWith(prefixes.cache)) {
      return cacheKey
    }
    const expiry = +(resp.headers.get(EXPIRY_HEADER) || 0)
    if (expiry > 0 && expiry < now) {
      return cacheKey
    }
    return false
  }
  const promises = allCacheResp.reduce((promises, resp) => {
    const key = shouldBeDeleted(resp)
    if (key) {
      promises.push(cacheStore.delete(key))
    }
    return promises
  }, [] as Promise<boolean>[])
  if (needToCleanKeys) {
    const allKeys = await cacheStore.keys()
    const clearKeys = allKeys.filter(req => !req.url.startsWith(prefixes.cache))
    for (const key of clearKeys) {
      promises.push(cacheStore.delete(key))
    }
  }
  await Promise.all(promises)
}

const noop = () => { }
export const maintainCache = cacheStorePromise ? maintainStorageCache : idbStore ? maintainDbCache : noop
export const cache = cacheStorePromise ? cacheStorageCache : idbStore ? idbCache : memAsyncCache

export const initCache = (options: CacheInitOptions) => {
  let uid = options.user_id
  if (/^[0-9a-f-]+$/.test(uid)) {
    uid = uid.split('-').map(u => (u ? parseInt(u, 16).toString(36) : '')).join('')
  }
  prefixes.session = `cache:${uid}:`
  prefixes.idb = `u:${uid}:`
  prefixes.cache = `/cache/u/${uid}/`
  if (maintainCache !== noop) {
    requestIdleCallback(maintainCache, { timeout: 30000 })
  }
  return cache
}

export default cache
